import { useFileUpload } from "@msuite/ui/file-upload";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { moment } from "@msuite/utils/date";
import { type ProjectInvoiceSchema, projectInvoiceSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { useUpload } from "@/api/files";
import { ProjectInvoiceForm } from "@/forms/project-invoice-form";
import { client } from "@/hono";

/** Props Interface */
interface AddProjectInvoiceModalProps extends ModalProps {
	projectId: string;
}

export function AddProjectInvoiceModal({
	isOpen,
	onClose,
	projectId,
}: AddProjectInvoiceModalProps) {
	/** Hooks */
	const qc = useQueryClient();
	const { t } = useTranslation();
	const methods = useForm({
		resolver: resolver(projectInvoiceSchema),
		defaultValues: {
			projectId,
			creationDate: moment().format("YYYY-MM-DD"),
			calculatedTo: null,
			state: "pending",
			type: "single-invoice",
			progressInvoiceNumber: 1,
			note: null,
		},
	});

	const { uploadFn } = useUpload();
	const controller = useFileUpload({
		uploadFn,
	});

	/** Functions */
	async function handleOnSubmit(json: ProjectInvoiceSchema) {
		const uploadResults = await controller.handleOnFileUpload();
		await client.projects[":projectId"].invoice.$post({
			param: {
				projectId,
			},
			json: {
				...json,
				uploadResults,
			},
		});
		onClose();
		await qc.invalidateQueries({
			predicate: invalidate(["invoices", projectId]),
		});
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("invoices.add_invoice")}
				onSubmit={methods.handleSubmit(handleOnSubmit, (e) => {
					console.error(e);
				})}
				data-cy="project-invoice-form"
			>
				<Modal.Body>
					<ProjectInvoiceForm controller={controller} />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
